import { ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/format";

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6 fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Money = ({ value, className = "", strong }) => (
  <span className={`num whitespace-nowrap ${strong ? "font-semibold text-slate-900" : "text-slate-700"} ${className}`}>
    {fmtMoney(value)} <span className="text-[10px] text-slate-400 font-medium">SAR</span>
  </span>
);

const BADGE = {
  pending: ["Pending", "bg-sky-50 text-sky-700 border-sky-200"],
  rejected: ["Rejected", "bg-red-50 text-red-700 border-red-200"],
  entered: ["Entered", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  reviewed: ["Reviewed", "bg-green-50 text-green-700 border-green-200"],
  query_raised: ["Query Raised", "bg-amber-50 text-amber-700 border-amber-200"],
  resolved: ["Resolved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  voided: ["Voided", "bg-red-50 text-red-700 border-red-200"],
  active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  inactive: ["Inactive", "bg-slate-100 text-slate-600 border-slate-200"],
  missing: ["No Attachment", "bg-orange-50 text-orange-700 border-orange-200"],
  resolution: ["Resolution", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  paid: ["Paid", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  refund_received: ["Refund Received", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  filed: ["Filed (Nil)", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  withdrawn: ["Withdrawn", "bg-slate-100 text-slate-500 border-slate-200"],
};

export const StatusBadge = ({ status, testid }) => {
  const [label, cls] = BADGE[status] || [status, "bg-slate-100 text-slate-600 border-slate-200"];
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
};

export const StatCard = ({ label, value, icon: Icon, hint, tone = "emerald", testid, delay = 0 }) => {
  const tones = { emerald: "text-emerald-700 bg-emerald-50", amber: "text-amber-700 bg-amber-50", sky: "text-sky-700 bg-sky-50", slate: "text-slate-700 bg-slate-100", red: "text-red-700 bg-red-50" };
  return (
    <div className="panel p-5 fade-up hover:border-emerald-300 transition-colors duration-200" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && <div className={`p-1.5 rounded ${tones[tone]}`}><Icon className="w-4 h-4" /></div>}
      </div>
      <div className="mt-3 text-2xl font-semibold num text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
};

export const EmptyState = ({ title = "No records found", subtitle, testid = "empty-state" }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center" data-testid={testid}>
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3"><Inbox className="w-5 h-5 text-slate-400" /></div>
    <div className="font-medium text-slate-700">{title}</div>
    {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
  </div>
);

export const Pager = ({ page, pageSize, total, onPage, testid = "pager" }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm text-slate-500" data-testid={testid}>
      <span className="num text-xs">{from}–{Math.min(page * pageSize, total)} of {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
        <span className="num text-xs">{page} / {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const Field = ({ label, required, children, hint, error }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
    <div className="mt-1.5">{children}</div>
    {error ? <span className="text-xs text-red-600 mt-1 block">{error}</span> : hint && <span className="text-[11px] text-slate-400 mt-1 block">{hint}</span>}
  </label>
);

export const Spinner = () => (
  <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
);
